import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "./utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
                "flex flex-1 justify-between items-center py-4 font-medium text-md text-slate-800 dark:text-slate-200 text-left hover:underline [&[data-state=open]>svg]:rotate-180 transition-all",
                className
            )}
            {...props}
        >
            {children}
            <ChevronDown className="w-6 h-6 transition-transform duration-200 shrink-0" />
        </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className={cn(
            "text-md text-slate-800 dark:text-slate-200 text-left transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
            "data-[state=closed]:overflow-hidden data-[state=open]:overflow-x-visible data-[state=open]:overflow-y-clip"
        )}
        {...props}
    >
        <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export interface InviteSummarySection {
    value: string;
    title: React.ReactNode;
    content: React.ReactNode;
}

/**
 * Grouped accordion for patient invite / QR summaries: outer heading plus one item per section
 * (e.g. Forms & monitoring, Injection, Contact). All sections default open.
 */
function InviteSummaryAccordion({
    heading,
    sections,
    className,
    defaultOpenAll = true,
}: {
    heading?: React.ReactNode;
    sections: InviteSummarySection[];
    className?: string;
    defaultOpenAll?: boolean;
}) {
    if (sections.length === 0) {
        return heading ? <div className={cn("space-y-3", className)}>{heading}</div> : null;
    }

    const defaultValue = defaultOpenAll ? sections.map((s) => s.value) : undefined;

    return (
        <div className={cn("space-y-3", className)}>
            {heading}
            <div className="border border-border rounded-lg overflow-hidden">
                <Accordion type="multiple" defaultValue={defaultValue} className="w-full">
                    {sections.map((section) => (
                        <AccordionItem
                            key={section.value}
                            value={section.value}
                            className="px-3 sm:px-4 border-border border-b last:border-b-0"
                        >
                            <AccordionTrigger className="py-3 font-semibold text-slate-800 dark:text-slate-200 text-sm hover:no-underline">
                                {section.title}
                            </AccordionTrigger>
                            <AccordionContent>{section.content}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, InviteSummaryAccordion };
